import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MIN_PASSWORD_LENGTH, PASSWORD_REGEX } from '../constant/auth.constant';
import { passwordConfirmationValidator } from '../../../core/validator/confirmPasswordValidator';
import { AuthApiService } from '../../../core/services/auth/auth-api.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  private _authService = inject(AuthApiService);
  private _fb: NonNullableFormBuilder = inject(FormBuilder).nonNullable;
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _toast = inject(ToastrService);

  resetPasswordForm!: FormGroup;

  token: string | null = null;
  isSubmitted = false;
  loading = false;

  ngOnInit(): void {
    this.token = this._route.snapshot.queryParams['token'];
    if (!this.token) {
      this._toast.error('Token de réinitialisation manquant ou invalide.', 'Erreur');
      this._router.navigate(['/']);
      return;
    }

    this.resetPasswordForm = this._fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH), Validators.pattern(PASSWORD_REGEX)]],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: passwordConfirmationValidator('password', 'confirmPassword'),
      }
    );
  }

  get password(): FormControl<string> {
    return this.resetPasswordForm.controls['password'] as FormControl<string>;
  }

  get confirmPassword(): FormControl<string> {
    return this.resetPasswordForm.controls['confirmPassword'] as FormControl<string>;
  }

  get passwordMismatch(): boolean {
    return this.resetPasswordForm.hasError('passwordMismatch') ?? false;
  }

  onSubmit(): void {
    this.isSubmitted = true;
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.loading = true;
    const { password } = this.resetPasswordForm.value;

    this._authService.resetPassword$(this.token!, password).subscribe({
      next: () => {
        this.loading = false;
        this._toast.success('Votre mot de passe a été réinitialisé avec succès.', 'Succès');
        this._router.navigate(['/login']);
      },
      error: () => {
        this.loading = false;
        this._toast.error('Le lien est expiré ou invalide. Veuillez refaire une demande.', 'Erreur');
      },
    });
  }
}
